// Einzelaufzeichnung eines Kassenvorgangs – rein, für Server, Export und Oberfläche. Jeder abgeschlossene Vorgang
// friert seine Aufzeichnung in pos_vorgang.record ein: Positionen mit Geschäftsvorfall und Steuerschlüssel, Steuer je
// Schlüssel, Zahlarten, Verweise. Daraus entstehen Vorgangsdaten, Kassenabschluss und Prüfer-Export – alle aus
// derselben Quelle, damit Bon, Signatur und Export übereinstimmen. Begriffe nach der Datensatzbeschreibung der Kassen
// (DSFinV-K 2.4: Vorgangstyp, Geschäftsvorfall, USt-Schlüssel, Zahlart), siehe docs/kasse/SPEZIFIKATION.md §2 und §5.
import { splitGross, type Cents, type RateBp } from '@shared/money'
import { emptySlots, kassenbelegProcessData, slotOfRate, type PaymentKind, type SlotAmounts, type Vorgangstyp } from './processData'

/** Geschäftsvorfälle (abschließende Liste, DSFinV-K 4.1.3). */
export const GV_TYPES = [
  'Umsatz',
  'Pfand',
  'PfandRueckzahlung',
  'Rabatt',
  'Aufschlag',
  'ZuschussEcht',
  'ZuschussUnecht',
  'TrinkgeldAG',
  'TrinkgeldAN',
  'EinzweckgutscheinKauf',
  'EinzweckgutscheinEinloesung',
  'MehrzweckgutscheinKauf',
  'MehrzweckgutscheinEinloesung',
  'Forderungsentstehung',
  'Forderungsaufloesung',
  'Anzahlungseinstellung',
  'Anzahlungsaufloesung',
  'Anfangsbestand',
  'Privatentnahme',
  'Privateinlage',
  'Geldtransit',
  'Lohnzahlung',
  'Einzahlung',
  'Auszahlung',
  'DifferenzSollIst',
] as const
export type GvTyp = (typeof GV_TYPES)[number]

/**
 * USt-Schlüssel (Anlage 2 der Datensatzbeschreibung, Stand 05.12.2024): 1 und 2 sind der zum Zeitpunkt der Erfassung
 * geltende allgemeine bzw. ermäßigte Satz; 21/22 die historischen 16 % und 5 % (2. Halbjahr 2020); 5–8 ohne Steuer.
 */
export const VAT_KEYS: Readonly<Record<number, { rateBp: number; text: string }>> = {
  1: { rateBp: 1900, text: 'Allgemeiner Steuersatz (§ 12 Abs. 1 UStG)' },
  2: { rateBp: 700, text: 'Ermäßigter Steuersatz (§ 12 Abs. 2 UStG)' },
  4: { rateBp: 550, text: 'Durchschnittssatz (§ 24 Abs. 1 Nr. 1 UStG)' },
  5: { rateBp: 0, text: 'Nicht Steuerbar' },
  6: { rateBp: 0, text: 'Umsatzsteuerfrei' },
  7: { rateBp: 0, text: 'UmsatzsteuerNichtErmittelbar' },
  8: { rateBp: 0, text: 'Ermäßigter Steuersatz (§ 12 Abs. 3 UStG)' },
  13: { rateBp: 1070, text: 'Historischer Durchschnittssatz (§ 24 Abs. 1 Nr. 3 UStG)' },
  21: { rateBp: 1600, text: 'Historischer allgemeiner Steuersatz (§ 12 Abs. 1 UStG)' },
  22: { rateBp: 500, text: 'Historischer ermäßigter Steuersatz (§ 12 Abs. 2 UStG)' },
}

/** Geschäftsvorfälle ohne Umsatzsteuer: immer Schlüssel 5 (nicht steuerbar), unabhängig vom Steuerschlüssel der Zeile. */
const NON_TAXABLE: ReadonlySet<GvTyp> = new Set<GvTyp>(['MehrzweckgutscheinKauf', 'MehrzweckgutscheinEinloesung', 'Anfangsbestand', 'Privatentnahme', 'Privateinlage', 'Geldtransit', 'DifferenzSollIst', 'Lohnzahlung'])

/**
 * USt-Schlüssel eines Satzes. 0 %: steuerfrei (6), mit dem Steuerschlüssel „Nullsteuersatz“ § 12 Abs. 3 (8); nicht
 * steuerbare Geschäftsvorfälle (Mehrzweckgutschein, Bargeld, Differenz) immer 5.
 */
export function vatKeyOf(rateBp: number, gvTyp: GvTyp, zeroRated = false): number {
  if (NON_TAXABLE.has(gvTyp)) return 5
  switch (rateBp) {
    case 1900:
      return 1
    case 700:
      return 2
    case 550:
      return 4
    case 1070:
      return 13
    case 1600:
      return 21
    case 500:
      return 22
    case 0:
      return zeroRated ? 8 : 6
    default:
      throw new RangeError(`Für den Steuersatz ${rateBp / 100} % ist kein USt-Schlüssel hinterlegt.`)
  }
}

export interface PriceFinding {
  /** Preis vor Rabatt (Menge × Einzelpreis) */
  baseGrossCents: number
  baseNetCents: number
  baseTaxCents: number
  /** Rabatt (negativ) */
  discountGrossCents: number
  discountNetCents: number
  discountTaxCents: number
}

export interface RecordLine {
  /** laufende Zeile im Vorgang, ab 1 */
  lineNo: number
  /** Code des Gutscheins (Verkauf und Einlösung) */
  voucherNo: string | null
  text: string
  gvTyp: GvTyp
  /** Merxo-Bezeichnung zum Geschäftsvorfall, z. B. „Leergut“ */
  gvName: string | null
  itemNo: string | null
  gtin: string | null
  groupId: string | null
  group: string | null
  /** Menge in Tausendstel (negativ bei Rücknahme, Storno und Gegenzeile) */
  qty: number
  unit: string | null
  /** Bruttopreis je Einheit (nach Rabatt) in 1/100 000 Euro – fünf Nachkommastellen wie im Export */
  unitGross5: number
  vatKey: number
  rateBp: number
  grossCents: number
  netCents: number
  taxCents: number
  priceFinding: PriceFinding | null
}

export interface RecordVat {
  vatKey: number
  rateBp: number
  grossCents: number
  netCents: number
  taxCents: number
}

/** Zahlart im Export: Bar, ECKarte, Kreditkarte, Unbar – Gutscheine sind keine Zahlart, sondern eine Position. */
export type RecordPaymentType = 'Bar' | 'Unbar' | 'ECKarte' | 'Kreditkarte' | 'Keine'

export interface RecordPayment {
  type: RecordPaymentType
  /** Merxo-Bezeichnung: „Bar“, „Karte“ */
  name: string
  amountCents: number
}

export interface RecordReference {
  lineNo: number | null
  type: 'Transaktion'
  /** Abschluss-Zeitpunkt (Z_ERSTELLUNG) des Originals – null, solange dessen Schicht offen ist */
  date: string | null
  registerSerial: string
  zNo: number | null
  bonId: number
  vorgangId: string
}

export interface RecordCustomer {
  id: string
  number: string | null
  name: string
  street: string | null
  zip: string | null
  city: string | null
  country: string | null
  vatId: string | null
}

export interface VorgangRecord {
  v: 1
  bonTyp: Vorgangstyp
  bonName: string
  storno: boolean
  /** Merxo-Zeiten des Vorgangs (ISO, UTC) */
  startedAt: string
  finishedAt: string
  operator: { id: string; name: string } | null
  customer: RecordCustomer | null
  totalCents: number
  note: string | null
  lines: RecordLine[]
  vat: RecordVat[]
  payments: RecordPayment[]
  references: RecordReference[]
}

/** Feld der Vorgangsdaten: Summe brutto je Steuersatz über alle Positionen (Gutschein-Einlösung mindert ihr Feld). */
export function slotsOf(lines: readonly Pick<RecordLine, 'rateBp' | 'grossCents'>[]): SlotAmounts {
  const slots = emptySlots()
  for (const line of lines) slots[slotOfRate(line.rateBp)] += line.grossCents
  return slots
}

export const processPaymentKind = (type: RecordPaymentType): PaymentKind => (type === 'Bar' ? 'Bar' : 'Unbar')

/** Vorgangsdaten eines abgeschlossenen Vorgangs (Kassenbeleg-V1). Ein Abbruch hat nur Nullen und keine Zahlung. */
export function processDataOf(record: Pick<VorgangRecord, 'bonTyp' | 'lines' | 'payments'>): string {
  if (record.bonTyp === 'AVBelegabbruch') return kassenbelegProcessData({ vorgangstyp: 'AVBelegabbruch', grossCents: emptySlots(), payments: [] })
  return kassenbelegProcessData({
    vorgangstyp: record.bonTyp,
    grossCents: slotsOf(record.lines),
    payments: record.payments.filter((payment) => payment.type !== 'Keine').map((payment) => ({ kind: processPaymentKind(payment.type), amountCents: payment.amountCents })),
  })
}

/** Steuer je Schlüssel aus den Positionen. */
export function vatOf(lines: readonly RecordLine[]): RecordVat[] {
  const byKey = new Map<number, RecordVat>()
  for (const line of lines) {
    const row = byKey.get(line.vatKey) ?? { vatKey: line.vatKey, rateBp: line.rateBp, grossCents: 0, netCents: 0, taxCents: 0 }
    row.grossCents += line.grossCents
    row.netCents += line.netCents
    row.taxCents += line.taxCents
    byKey.set(line.vatKey, row)
  }
  return [...byKey.values()].filter((row) => row.grossCents !== 0 || row.netCents !== 0 || row.taxCents !== 0).sort((a, b) => a.vatKey - b.vatKey)
}

/** Bargeld eines Vorgangs (für das Soll der Kasse): Summe der Barzahlungen. */
export const cashOf = (record: Pick<VorgangRecord, 'payments'>): number => record.payments.filter((payment) => payment.type === 'Bar').reduce((sum, payment) => sum + payment.amountCents, 0)

/** Summen eines Kassenabschlusses – nur Vorgangstyp „Beleg“ (Training und Abbruch zählen nicht). */
export interface ClosingTotals {
  /** je Geschäftsvorfall × Bezeichnung × USt-Schlüssel */
  businessCases: { gvTyp: GvTyp; gvName: string | null; vatKey: number; grossCents: number; netCents: number; taxCents: number }[]
  /** je Zahlart × Bezeichnung */
  payments: { type: RecordPaymentType; name: string; amountCents: number }[]
  /** Summe aller Zahlungen */
  totalPaymentsCents: number
  /** Summe der Barzahlungen */
  cashPaymentsCents: number
  /** Bargeldbestand in Euro nach Differenz und Geldtransit */
  cashBalanceCents: number
  vorgangCount: number
  firstBonId: number | null
  lastBonId: number | null
}

export function closingTotals(records: readonly { bonId: number; record: Pick<VorgangRecord, 'bonTyp' | 'lines' | 'payments'> }[]): ClosingTotals {
  const cases = new Map<string, ClosingTotals['businessCases'][number]>()
  const payments = new Map<string, ClosingTotals['payments'][number]>()
  let count = 0
  for (const { record } of records) {
    if (record.bonTyp !== 'Beleg') continue
    count += 1
    for (const line of record.lines) {
      const key = `${line.gvTyp}|${line.gvName ?? ''}|${line.vatKey}`
      const row = cases.get(key) ?? { gvTyp: line.gvTyp, gvName: line.gvName, vatKey: line.vatKey, grossCents: 0, netCents: 0, taxCents: 0 }
      row.grossCents += line.grossCents
      row.netCents += line.netCents
      row.taxCents += line.taxCents
      cases.set(key, row)
    }
    for (const payment of record.payments) {
      if (payment.type === 'Keine') continue
      const key = `${payment.type}|${payment.name}`
      const row = payments.get(key) ?? { type: payment.type, name: payment.name, amountCents: 0 }
      row.amountCents += payment.amountCents
      payments.set(key, row)
    }
  }
  const paymentRows = [...payments.values()].sort((a, b) => (a.type === b.type ? a.name.localeCompare(b.name) : a.type === 'Bar' ? -1 : b.type === 'Bar' ? 1 : a.type.localeCompare(b.type)))
  const totalPaymentsCents = paymentRows.reduce((sum, row) => sum + row.amountCents, 0)
  const cashPaymentsCents = paymentRows.filter((row) => row.type === 'Bar').reduce((sum, row) => sum + row.amountCents, 0)
  const bonIds = records.map((entry) => entry.bonId)
  return {
    businessCases: [...cases.values()].sort((a, b) => GV_TYPES.indexOf(a.gvTyp) - GV_TYPES.indexOf(b.gvTyp) || a.vatKey - b.vatKey || (a.gvName ?? '').localeCompare(b.gvName ?? '')),
    payments: paymentRows,
    totalPaymentsCents,
    cashPaymentsCents,
    cashBalanceCents: cashPaymentsCents,
    vorgangCount: count,
    firstBonId: bonIds.length > 0 ? Math.min(...bonIds) : null,
    lastBonId: bonIds.length > 0 ? Math.max(...bonIds) : null,
  }
}

/** Bruttopreis je Einheit mit fünf Nachkommastellen (1/100 000 €) aus Betrag und Menge (Tausendstel). */
export const unitGross5Of = (grossCents: number, qty: number): number => (qty === 0 ? grossCents * 1000 : Math.round((grossCents * 1_000_000) / qty))

/** Gegenvorgang (Storno nach Zahlung): alle Beträge und Mengen mit umgekehrtem Vorzeichen. */
export function negateLines(lines: readonly RecordLine[]): RecordLine[] {
  return lines.map((line) => ({
    ...line,
    qty: -line.qty,
    grossCents: -line.grossCents,
    netCents: -line.netCents,
    taxCents: -line.taxCents,
    priceFinding: line.priceFinding
      ? {
          baseGrossCents: -line.priceFinding.baseGrossCents,
          baseNetCents: -line.priceFinding.baseNetCents,
          baseTaxCents: -line.priceFinding.baseTaxCents,
          discountGrossCents: -line.priceFinding.discountGrossCents,
          discountNetCents: -line.priceFinding.discountNetCents,
          discountTaxCents: -line.priceFinding.discountTaxCents,
        }
      : null,
  }))
}

/** Netto und Steuer aus brutto bei einem Satz – über shared/money (genau einmal kaufmännisch gerundet). */
export function splitGrossAt(grossCents: number, rateBp: number): { netCents: number; taxCents: number } {
  const split = splitGross(grossCents as Cents, rateBp as RateBp)
  return { netCents: split.net, taxCents: split.tax }
}
